import { useCallback, useMemo } from 'react'
import { useExport } from './useExport'
import {
  createQuoteSheet,
  getQuoteSheet,
  type CreateQuoteSheetRequest,
} from '@/api/quoteSheets'
import { toast } from '@/stores/useToastStore'

const MAX_LINE_ITEMS = 500

/**
 * Hook to export a quote sheet for selected plan lines.
 * Uses the generic useExport hook with quote-sheet-specific endpoints.
 */
export function useQuoteSheetExport(planId: string, versionId: string | null) {
  const createFn = useCallback(
    (request?: CreateQuoteSheetRequest) =>
      createQuoteSheet(planId, versionId!, request!),
    [planId, versionId]
  )

  const getFn = useCallback((exportId: string) => getQuoteSheet(exportId), [])

  const exportHook = useExport<CreateQuoteSheetRequest>({
    createExportFn: createFn,
    getExportFn: getFn,
    filenamePrefix: 'quote-sheet',
    fileExtension: 'xlsm',
  })

  const { startExport } = exportHook

  const exportQuoteSheet = useCallback(
    (request: CreateQuoteSheetRequest) => {
      if (!versionId) return

      if (request.line_item_ids.length > MAX_LINE_ITEMS) {
        toast.error(
          `Cannot create quote sheet for more than ${MAX_LINE_ITEMS} items`
        )
        return
      }

      startExport(request)
    },
    [versionId, startExport]
  )

  const canExport = useMemo(() => !!versionId, [versionId])

  return {
    ...exportHook,
    exportQuoteSheet,
    canExport,
  }
}
